// Step 1 tells the path to COD as six chapters, each one sentence InRange can say out
// loud to Amazon; steps in the same chapter happen at the same time. The chapter count
// must agree with CHAPTER_COUNT in src/inrange_agents/amazon_blueprint.py.
import type { PathStep } from "./pathSteps";

export interface Chapter {
  title: string;
  /** One line on what happens. */
  line: string;
  /** The chapter told as a story. */
  story: string;
}

export const CHAPTERS: Chapter[] = [
  {
    title: "Size it up",
    line: "Max-out design to the DNO, roof surveys, SPPAs negotiated.",
    story:
      "We started by drawing the biggest system each roof could take, and sent it to the DNO as the grid application. Meanwhile Amazon got our teams onto the roofs for the site and roof surveys, and both legal teams worked through the SPPAs.",
  },
  {
    title: "Sign and measure",
    line: "SPPAs signed, guarantee delivered, landlord packs out, structural results and grid offers in.",
    story:
      "The SPPAs were signed and Amazon.com, Inc. delivered its parent company guarantee for the whole portfolio. We sent the preliminary consent packs, with the preliminary designs, to Amazon's asset managers to pass on to each landlord; a building Amazon owns has no landlord pack. Our engineers turned the surveys into structural assessments, which set how much each roof can really carry, while the DNO came back with grid offers.",
  },
  {
    title: "Design to fit",
    line: "Proceed toward a detailed design that accounts for all site-specific restrictions toward final approval.",
    story:
      "With the real roof capacity known, Amazon signs off the structural study and hands over what only Amazon has: where the MFL walls are, the fire strategy, the roof warranties and the rest. Together we inspect every roof, and we produce the detailed design.",
  },
  {
    title: "Approve",
    line: "Amazon technical and insurance reviews; the detailed LL pack and LL sign-off.",
    story:
      "Our detailed design goes to Amazon for its technical and insurance reviews, and is also the landlord's detailed pack. Amazon has up to 104 Business Days to give design approval, with no deemed approval, and seeks the landlord's sign-off by the Schedule 1 Landlord Approval Date.",
  },
  {
    title: "Build",
    line: "Notice to Proceed, mobilise onto a repaired roof, install.",
    story:
      "With design approval, the landlord's sign-off and Notice to Proceed, we mobilise onto a roof that has been inspected and repaired, and install at about two months per MWp. Installation carries on through the winter, but nothing may interrupt the power from 1 October to 15 January and cranes are off site by the end of 14 October.",
  },
  {
    title: "Switch on",
    line: "Roof re-inspected, connect, commission, COD checklist.",
    story:
      "An independent inspector checks the roof again with a leak test, Amazon and the landlord can review it, and we repair any damage at our cost. Meanwhile we connect to the grid, commission and run the performance test, then send Amazon the COD checklist. Amazon has 10 days to acknowledge it, or COD is deemed approved.",
  },
];

/** Steps the story shows that no building's record answers: the negotiation before
 * signing, the connection date, a capacity change and planning. */
export const STORY_STEPS: PathStep[] = [
  {
    id: "negotiate",
    chapter: 0,
    lane: "legal",
    owner: "both",
    title: "SPPA negotiation",
    desc: "Both legal teams",
    deps: [],
    days: null,
    subs: [
      "SPPA and Schedule 1 dates agreed per building",
      "Runs alongside the grid application and surveys",
    ],
    needs: [],
    source: "SPPA",
  },
  {
    id: "capacity",
    chapter: 2,
    lane: "legal",
    owner: "both",
    title: "Capacity change agreed",
    desc: "If roof or grid allow less than the PPA",
    deps: ["structural", "gridoffer"],
    days: null,
    subs: [
      "Where the achievable size is below the PPA size, agree the new size and any SPPA amendment",
    ],
    needs: [],
    source: "Proposed",
  },
  {
    id: "planning",
    chapter: 2,
    lane: "legal",
    owner: "inrange",
    title: "Planning & building control",
    desc: "Permitted development or consent",
    deps: ["structural"],
    days: null,
    subs: [
      "Confirm per building whether the install falls under permitted development or needs consent",
      "Feeds the list of approvals Amazon needs before design approval (MSPPA s3.3(b))",
    ],
    needs: [],
    source: "Proposed",
  },
  {
    id: "conndate",
    chapter: 2,
    lane: "grid",
    owner: "third",
    ownerLabel: "DNO",
    title: "Connection date before COD",
    desc: "Check the offer's energisation date",
    deps: ["gridoffer"],
    days: null,
    subs: [
      "The offer carries a connection date. If it falls after the committed COD, build speed cannot fix it",
    ],
    needs: [],
    source: "Proposed",
  },
];
